/**
 * NetworkTal Analytics Utility
 *
 * Centralizes all tracking calls to App Insights and GA4.
 *
 * Usage:
 *   import { trackSignup, trackBookingStep } from '@/utils/analytics';
 *   trackSignup('email', userId);
 *   trackBookingStep(1, { sessionType: 'Career Coaching', sessionPrice: 50 });
 *
 * @see /docs/ANALYTICS_IMPLEMENTATION_PLAN.md for full documentation
 */

const DEBUG = process.env.NODE_ENV === "development";

// ============================================
// App Insights Helpers
// ============================================

/**
 * Track a custom event to App Insights
 * @param {string} eventName - Event name (e.g., 'Booking_Started')
 * @param {Object} properties - Additional properties to log
 */
export function trackEvent(eventName, properties = {}) {
  if (window.appInsights) {
    window.appInsights.trackEvent({
      name: eventName,
      properties: {
        ...properties,
        timestamp: new Date().toISOString(),
      },
    });
  }

  if (DEBUG) {
    console.log("[Analytics] Event:", eventName, properties);
  }
}

/**
 * Track an exception to App Insights
 * @param {Error} error - The error object
 * @param {Object} properties - Additional context
 */
export function trackException(error, properties = {}) {
  if (window.appInsights) {
    window.appInsights.trackException({
      exception: error,
      properties,
    });
  }

  if (DEBUG) {
    console.error("[Analytics] Exception:", error, properties);
  }
}

/**
 * Set the authenticated user ID for correlation
 * @param {string} userId - Firebase user ID
 */
export function setUserId(userId) {
  if (window.appInsights && userId) {
    window.appInsights.setAuthenticatedUserContext(userId);
  }
}

/**
 * Clear the authenticated user context (on logout)
 */
export function clearUserId() {
  if (window.appInsights) {
    window.appInsights.clearAuthenticatedUserContext();
  }
}

// ============================================
// GA4 Helpers
// ============================================

/**
 * Send an event to GA4
 * @param {string} eventName - GA4 event name
 * @param {Object} params - Event parameters
 */
export function trackGA4Event(eventName, params = {}) {
  if (window.gtag) {
    window.gtag("event", eventName, params);
  }

  if (DEBUG) {
    console.log("[GA4] Event:", eventName, params);
  }
}

// ============================================
// Authentication Events
// ============================================

/**
 * Track successful signup
 * @param {'email' | 'google'} method - Signup method
 * @param {string} userId - New user's ID
 */
export function trackSignup(method, userId) {
  trackEvent("Auth_Signup_Completed", { method, userId });
  trackGA4Event("sign_up", { method });
  setUserId(userId);
}

/**
 * Track failed signup attempt
 * @param {'email' | 'google'} method - Signup method
 * @param {string} errorMessage - Error description
 */
export function trackSignupFailed(method, errorMessage) {
  trackEvent("Auth_Signup_Failed", { method, errorMessage });
}

/**
 * Track successful login
 * @param {'email' | 'google'} method - Login method
 * @param {string} userId - User's ID
 */
export function trackLogin(method, userId) {
  trackEvent("Auth_Login_Completed", { method, userId });
  trackGA4Event("login", { method });
  setUserId(userId);
}

/**
 * Track failed login attempt
 * @param {'email' | 'google'} method - Login method
 * @param {string} errorMessage - Error description
 */
export function trackLoginFailed(method, errorMessage) {
  trackEvent("Auth_Login_Failed", { method, errorMessage });
}

/**
 * Track user logout
 * @param {string} userId - User's ID (captured before state clear)
 */
export function trackLogout(userId) {
  trackEvent("Auth_Logout", { userId });
  clearUserId();
}

// ============================================
// Browse / Discovery Events
// ============================================

function safeErrorMessage(error) {
  if (!error) return "unknown";
  if (typeof error === "string") return error.slice(0, 300);
  if (error?.message) return String(error.message).slice(0, 300);
  try {
    return JSON.stringify(error).slice(0, 300);
  } catch {
    return "unknown";
  }
}

/**
 * Track listing fetch failures (trainers/sessions) to AppInsights + GA4.
 *
 * @param {'trainers' | 'sessions'} listingType
 * @param {Error|any} error
 * @param {Object} context
 */
export function trackListingFetchFailed(listingType, error, context = {}) {
  const errorMessage = safeErrorMessage(error);
  const properties = {
    listingType,
    errorMessage,
    ...context,
  };

  trackEvent("Browse_Listing_Fetch_Failed", properties);

  // AppInsights gets the full exception object when available.
  if (error instanceof Error) {
    trackException(error, properties);
  } else {
    trackException(new Error(errorMessage), properties);
  }

  trackGA4Event("listing_fetch_failed", {
    listing_type: listingType,
    error_message: errorMessage,
    path: context?.path,
  });
}

/**
 * Track trainers list page view
 * @param {number} resultCount - Number of trainers shown
 * @param {boolean} filterApplied - Whether filters were active
 */
export function trackTrainersViewed(resultCount, filterApplied = false) {
  trackEvent("Browse_Trainers_Viewed", { resultCount, filterApplied });
  trackGA4Event("view_item_list", {
    item_list_id: "trainers",
    item_list_name: "Trainers",
  });
}

/**
 * Track sessions list page view
 * @param {number} resultCount - Number of sessions shown
 * @param {boolean} filterApplied - Whether filters were active
 */
export function trackSessionsViewed(resultCount, filterApplied = false) {
  trackEvent("Browse_Sessions_Viewed", { resultCount, filterApplied });
  trackGA4Event("view_item_list", {
    item_list_id: "sessions",
    item_list_name: "Sessions",
  });
}

/**
 * Track trainer filter applied
 * @param {'name' | 'areas'} filterType - Type of filter
 * @param {string} filterValue - The filter value
 */
export function trackTrainersFiltered(filterType, filterValue) {
  trackEvent("Browse_Trainers_Filtered", { filterType, filterValue });
}

/**
 * Track session filter applied
 * @param {'title' | 'areas' | 'price'} filterType - Type of filter
 * @param {string} filterValue - The filter value
 */
export function trackSessionsFiltered(filterType, filterValue) {
  trackEvent("Browse_Sessions_Filtered", { filterType, filterValue });
}

// ============================================
// Trainer Profile Events
// ============================================

/**
 * Track trainer profile view
 * @param {string} trainerId - Trainer's ID
 * @param {string} trainerName - Trainer's display name
 * @param {'direct' | 'trainers_list' | 'sessions_list'} source - How user arrived
 */
export function trackTrainerProfileViewed(
  trainerId,
  trainerName,
  source = "direct"
) {
  trackEvent("Trainer_Profile_Viewed", { trainerId, trainerName, source });
  trackGA4Event("view_item", {
    currency: "USD",
    value: 0,
    items: [
      {
        item_id: trainerId,
        item_name: trainerName,
        item_category: "trainer",
      },
    ],
  });
}

/**
 * Track session landing page view
 * @param {string} sessionId - Session ID
 * @param {string} sessionTitle - Session title
 * @param {string} trainerId - Trainer's ID
 * @param {string} trainerName - Trainer's display name
 * @param {Object} options - Additional options
 * @param {number} options.price - Session price
 * @param {number} options.duration - Session duration in minutes
 * @param {string} options.currency - Currency code (default: USD)
 */
export function trackSessionPageViewed(
  sessionId,
  sessionTitle,
  trainerId,
  trainerName,
  options = {}
) {
  const { price = 0, duration, currency = "USD" } = options;
  trackEvent("Session_Page_Viewed", {
    sessionId,
    sessionTitle,
    trainerId,
    trainerName,
    price,
    duration,
    currency,
  });
  trackGA4Event("view_item", {
    currency,
    value: price,
    items: [
      {
        item_id: sessionId,
        item_name: sessionTitle,
        item_category: "session",
        item_brand: trainerName,
        price,
      },
    ],
  });
}

/**
 * Track "Book Session" button click
 * @param {string} trainerId - Trainer's ID
 * @param {boolean} isAuthenticated - Whether user is logged in
 * @param {Object} options - Additional context
 * @param {string} options.sessionId - Session ID (if from session page)
 * @param {string} options.sessionTitle - Session title
 * @param {number} options.price - Session price
 * @param {string} options.currency - Currency code
 * @param {string} options.source - Where clicked (trainer_profile, session_landing)
 */
export function trackBookSessionClicked(
  trainerId,
  isAuthenticated,
  options = {}
) {
  const {
    sessionId,
    sessionTitle,
    price,
    currency = "USD",
    source = "trainer_profile",
  } = options;
  trackEvent("Trainer_BookSession_Clicked", {
    trainerId,
    isAuthenticated,
    sessionId,
    sessionTitle,
    price,
    currency,
    source,
  });

  // GA4 select_item event for session landing clicks
  if (sessionId) {
    trackGA4Event("select_item", {
      item_list_id: "session_landing",
      item_list_name: "Session Landing Page",
      currency,
      value: price || 0,
      items: [
        {
          item_id: sessionId,
          item_name: sessionTitle,
          price: price || 0,
        },
      ],
    });
  }
}

/**
 * Track "Request Session" button click
 * @param {string} trainerId - Trainer's ID
 * @param {Object} options - Additional context
 * @param {string} options.sessionId - Session ID (if from session page)
 * @param {string} options.sessionTitle - Session title
 * @param {string} options.source - Where clicked (trainer_profile, session_landing)
 */
export function trackRequestSessionClicked(trainerId, options = {}) {
  const { sessionId, sessionTitle, source = "trainer_profile" } = options;
  trackEvent("Trainer_RequestSession_Clicked", {
    trainerId,
    sessionId,
    sessionTitle,
    source,
  });
}

// ============================================
// Booking Funnel Events
// ============================================

/**
 * Track booking dialog opened
 * @param {string} trainerId - Trainer's ID
 * @param {string} sessionId - Pre-selected session ID (if any)
 */
export function trackBookingStarted(trainerId, sessionId = null) {
  trackEvent("Booking_Started", { trainerId, sessionId });
}

/**
 * Track booking funnel step completion
 * @param {1 | 2 | 3 | 4} step - Step number
 * @param {Object} data - Step-specific data
 *
 * Step 1 data: { sessionId, sessionType, sessionPrice, isTrial }
 * Step 2 data: { selectedDate, selectedTime }
 * Step 3 data: { communicationTool }
 * Step 4 data: { trainerId, sessionId, sessionTitle, price, currency }
 */
export function trackBookingStep(step, data) {
  const eventNames = {
    1: "Booking_Step1_SessionType_Selected",
    2: "Booking_Step2_DateTime_Selected",
    3: "Booking_Step3_CommTool_Selected",
    4: "Booking_Step4_Summary_Viewed",
  };

  trackEvent(eventNames[step], data);

  // GA4 e-commerce events
  if (step === 1 && data.sessionPrice !== undefined) {
    trackGA4Event("add_to_cart", {
      currency: "USD",
      value: data.sessionPrice,
      items: [
        {
          item_id: data.sessionId,
          item_name: data.sessionType,
          price: data.sessionPrice,
          quantity: 1,
        },
      ],
    });
  }

  if (step === 4) {
    trackGA4Event("begin_checkout", {
      currency: data.currency || "USD",
      value: data.price || 0,
      items: [
        {
          item_id: data.sessionId,
          item_name: data.sessionTitle,
          price: data.price,
          quantity: 1,
        },
      ],
    });
  }
}

/**
 * Track booking submission (before payment)
 * @param {Object} data - { trainerId, sessionId, isFreeTrial, price }
 */
export function trackBookingSubmitted(data) {
  trackEvent("Booking_Submitted", data);
}

/**
 * Track booking failure
 * @param {string} trainerId - Trainer's ID
 * @param {string} errorMessage - Error description
 */
export function trackBookingFailed(trainerId, errorMessage) {
  trackEvent("Booking_Failed", { trainerId, errorMessage });
}

// ============================================
// Payment Events
// ============================================

/**
 * Track payment page view
 * @param {number} amount - Amount in cents
 * @param {string} currency - Currency code
 * @param {string} trainerId - Trainer's ID
 */
export function trackPaymentPageViewed(amount, currency, trainerId) {
  trackEvent("Payment_Page_Viewed", { amount, currency, trainerId });
}

/**
 * Track payment submission
 * @param {number} amount - Amount in cents
 * @param {string} currency - Currency code
 */
export function trackPaymentSubmitted(amount, currency) {
  trackEvent("Payment_Submitted", { amount, currency });
}

/**
 * Track successful payment
 * @param {Object} data - Payment details
 * @param {string} data.paymentIntentId - Stripe payment intent ID
 * @param {number} data.amount - Amount in cents
 * @param {string} data.currency - Currency code
 * @param {string} data.trainerId - Trainer's ID
 * @param {string} data.sessionId - Session ID
 * @param {string} data.sessionTitle - Session title
 */
export function trackPaymentSucceeded(data) {
  trackEvent("Payment_Succeeded", data);

  // GA4 purchase event - critical for e-commerce reporting
  trackGA4Event("purchase", {
    transaction_id: data.paymentIntentId,
    value: data.amount / 100, // Convert cents to dollars
    currency: (data.currency || "usd").toUpperCase(),
    items: [
      {
        item_id: data.sessionId,
        item_name: data.sessionTitle,
        item_category: "session",
        price: data.amount / 100,
        quantity: 1,
      },
    ],
  });
}

/**
 * Track failed payment
 * @param {number} amount - Amount in cents
 * @param {string} errorMessage - Error description
 */
export function trackPaymentFailed(amount, errorMessage) {
  trackEvent("Payment_Failed", { amount, errorMessage });
}

/**
 * Track processing payment
 * @param {string} paymentIntentId - Stripe payment intent ID
 */
export function trackPaymentProcessing(paymentIntentId) {
  trackEvent("Payment_Processing", { paymentIntentId });
}

// ============================================
// Session & Review Events
// ============================================

/**
 * Track session marked as completed
 * @param {string} sessionId - Session ID
 * @param {string} trainerId - Trainer's ID
 */
export function trackSessionCompleted(sessionId, trainerId) {
  trackEvent("Session_Completed", { sessionId, trainerId });
}

/**
 * Track session cancelled
 * @param {string} sessionId - Session ID
 * @param {string} trainerId - Trainer's ID
 * @param {string} reason - Cancellation reason
 */
export function trackSessionCancelled(sessionId, trainerId, reason = "") {
  trackEvent("Session_Cancelled", { sessionId, trainerId, reason });

  // GA4 refund event
  trackGA4Event("refund", {
    transaction_id: sessionId,
  });
}

/**
 * Track review dialog opened
 * @param {string} sessionId - Session ID
 * @param {string} trainerId - Trainer's ID
 */
export function trackReviewDialogOpened(sessionId, trainerId) {
  trackEvent("Review_Dialog_Opened", { sessionId, trainerId });
}

/**
 * Track review submitted
 * @param {string} sessionId - Session ID
 * @param {string} trainerId - Trainer's ID
 * @param {number} rating - Rating given (1-5)
 */
export function trackReviewSubmitted(sessionId, trainerId, rating) {
  trackEvent("Review_Submitted", { sessionId, trainerId, rating });
}

// ============================================
// Page View Tracking (for SPA)
// ============================================

/**
 * Track page view (call from router afterEach)
 * @param {string} pagePath - Route path
 * @param {string} pageTitle - Page title
 */
export function trackPageView(pagePath, pageTitle) {
  trackEvent("Page_Viewed", { pagePath, pageTitle });
  trackGA4Event("page_view", {
    page_path: pagePath,
    page_title: pageTitle,
  });
}
